import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { ArrowLeft, Check, CreditCard, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../lib/authContext';
import api from '../lib/api';

import { MagnifyingGlass } from '@phosphor-icons/react';
import { setFormDirty } from '../lib/updateGuard';

function storageKey(draftId) { return draftId ? `foretour:wizard:${draftId}` : 'foretour:wizard:new'; }
function loadWizardState(draftId) {
  try { return JSON.parse(localStorage.getItem(storageKey(draftId))) || {}; } catch { return {}; }
}
function saveWizardState(draftId, s) { localStorage.setItem(storageKey(draftId), JSON.stringify(s)); }
function clearWizardState(draftId) { localStorage.removeItem(storageKey(draftId)); }

const STEPS = ['type', 'details', 'size', 'rounds', 'summary'];

const MAX_COURSE_ROWS = 100;

function WizardCoursePicker({ courses, value, requestName, onSelect, onRequestCourse, onRequestNameChange, onImportCourse, roundIndex, testIdPrefix }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const debRef = useRef(null);
  const containerRef = useRef(null);
  // Golf API fallback state
  const [apiResults, setApiResults] = useState(null);
  const [apiSearching, setApiSearching] = useState(false);
  const [apiImporting, setApiImporting] = useState(null); // courseID being imported
  const [apiError, setApiError] = useState('');

  const selectedCourse = value ? courses.find(c => c.id === value) : null;

  const handleSearchChange = useCallback((val) => {
    setSearch(val);
    setApiResults(null);
    setApiError('');
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => setDebounced(val), 150);
  }, []);

  const filtered = useMemo(() => {
    if (!debounced.trim()) return courses;
    const q = debounced.trim().toLowerCase();
    return courses.filter(c => c.name.toLowerCase().includes(q) || (c.region || '').toLowerCase().includes(q));
  }, [courses, debounced]);

  const displayed = filtered.slice(0, MAX_COURSE_ROWS);
  const hiddenCount = filtered.length - displayed.length;

  const handleApiSearch = async () => {
    const q = debounced.trim() || search.trim();
    if (q.length < 2) return;
    setApiSearching(true);
    setApiError('');
    try {
      const { data } = await api.post('/organiser/golf-api/search', { q });
      setApiResults(data.api_results || []);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        setApiError(typeof detail === 'string' ? detail : "You've used all 10 course searches today. Try \"Request a course\" instead.");
      } else {
        setApiError(typeof detail === 'string' ? detail : (detail?.message || 'Search failed'));
      }
    } finally { setApiSearching(false); }
  };

  const handleApiImport = async (apiCourseId, courseName) => {
    setApiImporting(apiCourseId);
    setApiError('');
    try {
      const { data } = await api.post('/organiser/golf-api/import', { api_course_id: apiCourseId, round_index: roundIndex ?? 0 });
      if (data.ok) {
        if (data.already_imported) {
          setApiError('Already imported');
        }
        if (onImportCourse) onImportCourse(data.course_id, data.course);
        setOpen(false);
      } else if (data.code === 'validation_failed') {
        setApiError(`We couldn't import this course.`);
        setApiResults(prev => prev ? prev.map(r => r.id === apiCourseId ? { ...r, failed: true, failName: courseName } : r) : prev);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setApiError(typeof detail === 'string' ? detail : (detail?.message || 'Import failed'));
    } finally { setApiImporting(null); }
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!open) {
    return (
      <div>
        <button type="button" onClick={() => { setOpen(true); setSearch(''); setDebounced(''); setApiResults(null); setApiError(''); }}
          className="w-full h-11 px-3 rounded-lg border border-[#E1E6E4] text-sm bg-white text-left truncate flex items-center"
          data-testid={testIdPrefix}>
          <span className={selectedCourse ? 'text-[#1D242B]' : 'text-[#455561]'}>
            {selectedCourse ? selectedCourse.name : value ? 'Request a course' : 'Request a course'}
          </span>
        </button>
        {!value && (
          <Input className="mt-2" placeholder="Course name to request" value={requestName || ''}
            onChange={e => onRequestNameChange(e.target.value)} data-testid={`${testIdPrefix}-req`} />
        )}
      </div>
    );
  }

  const showApiFallback = debounced.trim().length >= 3 && !apiResults;

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B8C7C0]" />
        <Input autoFocus placeholder="Search courses or clubs..." value={search}
          onChange={e => handleSearchChange(e.target.value)}
          className="pl-9 h-11 rounded-lg border-[#1B4332] bg-white text-sm"
          data-testid={`${testIdPrefix}-search`} />
      </div>
      <div className="mt-1 border border-[#E1E6E4] rounded-lg bg-white max-h-[260px] overflow-y-auto shadow-lg">
        {displayed.map(c => (
          <button key={c.id} type="button" onClick={() => { onSelect(c.id); setOpen(false); }}
            className="w-full text-left px-3 py-3 hover:bg-[#F4F6F5] border-b border-[#F4F6F5] last:border-0 flex items-center min-h-[44px]"
            data-testid={`${testIdPrefix}-opt-${c.id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#1D242B] truncate">{c.name}</p>
              {c.region && <p className="text-xs text-[#455561]">{c.region}</p>}
            </div>
            <span className="text-xs text-[#B8C7C0] ml-2 flex-shrink-0">Par {c.par}</span>
          </button>
        ))}
        {hiddenCount > 0 && (
          <p className="text-center text-xs text-[#B8C7C0] py-2">{hiddenCount} more — refine your search</p>
        )}

        {/* DB empty + no API results yet → "Can't find it?" link */}
        {showApiFallback && (
          <button type="button" onClick={handleApiSearch} disabled={apiSearching}
            className="w-full text-center px-3 py-3 hover:bg-[#F4F6F5] text-sm text-[#1B4332] font-semibold"
            data-testid={`${testIdPrefix}-api-search`}>
            {apiSearching
              ? <span className="inline-block w-4 h-4 border-2 border-[#1B4332] border-t-transparent rounded-full animate-spin" />
              : "Can't find it? Search all courses"}
          </button>
        )}

        {/* API search results */}
        {apiResults && apiResults.length > 0 && (
          <div className="border-t border-[#E1E6E4]">
            <p className="text-[10px] font-semibold text-[#455561] uppercase tracking-wide px-3 pt-2 pb-1">External results</p>
            {apiResults.map(r => (
              <button key={r.id} type="button"
                onClick={() => {
                  if (r.failed) return;
                  if (r.already_imported && r.course_id) {
                    const existing = courses.find(c => c.id === r.course_id);
                    if (existing) {
                      onSelect(r.course_id);
                      setOpen(false);
                    } else {
                      handleApiImport(r.id, r.name);
                    }
                  } else {
                    handleApiImport(r.id, r.name);
                  }
                }}
                disabled={!!apiImporting || r.failed}
                className={`w-full text-left px-3 py-3 hover:bg-[#F4F6F5] border-b border-[#F4F6F5] last:border-0 flex items-center min-h-[44px] ${apiImporting ? 'opacity-60 cursor-not-allowed' : ''}`}
                data-testid={`${testIdPrefix}-api-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1D242B] truncate">{r.name}</p>
                  <p className="text-xs text-[#455561] truncate">{r.club} · {r.location}</p>
                  {r.failed && (
                    <button type="button"
                      onClick={(e) => { e.stopPropagation(); onRequestCourse(); onRequestNameChange(r.failName || search); setOpen(false); }}
                      className="text-xs text-[#D32F2F] font-semibold mt-0.5 hover:underline"
                      data-testid={`${testIdPrefix}-api-${r.id}-fallback`}>
                      Couldn't import — request it instead
                    </button>
                  )}
                </div>
                {r.already_imported
                  ? <span className="text-[10px] text-[#2E7D32] font-semibold ml-2 flex-shrink-0">Already imported</span>
                  : apiImporting === r.id
                    ? <span className="text-[10px] text-[#1B4332] font-semibold ml-2 flex-shrink-0">Importing…</span>
                    : null}
              </button>
            ))}
          </div>
        )}
        {apiResults && apiResults.length === 0 && (
          <p className="text-center text-xs text-[#B8C7C0] py-3">No external courses found</p>
        )}

        {apiError && <p className="text-xs text-[#D32F2F] px-3 py-2" data-testid={`${testIdPrefix}-api-error`}>{apiError}</p>}

        {displayed.length === 0 && !apiResults && !showApiFallback && (
          <p className="text-center text-xs text-[#B8C7C0] py-3">No courses match</p>
        )}
        <button type="button" onClick={() => { onRequestCourse(); setOpen(false); }}
          className="w-full text-left px-3 py-3 hover:bg-[#FFF8E1] border-t border-[#E1E6E4] flex items-center min-h-[44px] sticky bottom-0 bg-white"
          data-testid={`${testIdPrefix}-request`}>
          <span className="text-sm font-semibold text-[#1B4332]">Request a course</span>
        </button>
      </div>
    </div>
  );
}

export default function Wizard() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { organiser } = useAuth();

  // Update guard: mark wizard as dirty while user is editing
  useEffect(() => { setFormDirty('wizard', true); return () => setFormDirty('wizard', false); }, []);

  const draftIdParam = params.get('draft_id') || null;
  const [draftId, setDraftId] = useState(draftIdParam);
  const [step, setStep] = useState(params.get('step') || 'type');
  const [state, setState] = useState(() => loadWizardState(draftIdParam));
  const [courses, setCourses] = useState([]);
  const [settings, setSettings] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [paying, setPaying] = useState(false);
  const savingRef = useRef(false);
  const idempotencyKey = useRef(crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));
  const [latestPayment, setLatestPayment] = useState(null);
  const [loadingDraft, setLoadingDraft] = useState(!!draftIdParam);
  const [showCorporateMsg, setShowCorporateMsg] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null); // null | {percent_off, discount_pence, discounted_total_pence, full_price_pence}
  const [promoError, setPromoError] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);

  useEffect(() => { api.get('/courses').then(r => setCourses(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get('/settings/public').then(r => setSettings(r.data)).catch(() => {}); }, []);

  // Load existing draft when resuming
  useEffect(() => {
    if (!draftIdParam) { setLoadingDraft(false); return; }
    api.get(`/organiser/drafts/${draftIdParam}`).then(r => {
      const d = r.data;
      const hydrated = {
        draftId: draftIdParam,
        event_type: d.event_type || 'trip',
        name: d.name || '',
        start_date: d.start_date || '',
        end_date: d.end_date || '',
        player_count: d.player_count || 0,
        round_count: d.round_count || 0,
        rounds: (d.draft_rounds || []).map(rd => ({
          format: rd.format || 'stableford',
          scoring_mode: rd.scoring_mode || 'individual',
          course_id: rd.course_id || null,
          request_course_name: rd.request_course_name || null,
          tee_name: rd.tee_name || null,
        })),
        agreement_accepted: d.agreement_accepted || false,
      };
      setState(hydrated);
      saveWizardState(draftIdParam, hydrated);
      setDraftId(draftIdParam);
      if (d.latest_payment) setLatestPayment(d.latest_payment);
    }).catch(() => { toast.error('Failed to load draft'); }).finally(() => setLoadingDraft(false));
  }, [draftIdParam]);

  const update = useCallback((patch) => {
    setState(prev => { const next = { ...prev, ...patch }; saveWizardState(draftId, next); return next; });
  }, [draftId]);

  const goStep = (s) => {
    setStep(s);
    const qp = new URLSearchParams({ step: s });
    if (draftId) qp.set('draft_id', draftId);
    navigate(`/wizard/new?${qp}`, { replace: true });
  };

  // Compute pricing when size is known
  useEffect(() => {
    if (state.player_count >= 2 && state.round_count >= 1) {
      api.post('/pricing/compute', { players: state.player_count, rounds: state.round_count })
        .then(r => setPricing(r.data)).catch(() => {});
    }
  }, [state.player_count, state.round_count]);

  const handleSave = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const rounds = (state.rounds || []).map(r => ({
        format: r.format || 'stableford',
        scoring_mode: r.scoring_mode || 'individual',
        course_id: r.course_id || null,
        request_course_name: r.request_course_name || null,
        tee_name: r.tee_name || null,
      }));
      let savedDraftId = draftId;
      if (draftId) {
        await api.patch(`/organiser/drafts/${draftId}`, {
          name: state.name, start_date: state.start_date, end_date: state.end_date,
          player_count: state.player_count, round_count: state.round_count, rounds,
        });
      } else {
        const resp = await api.post('/organiser/drafts', {
          event_type: state.event_type || 'trip',
          name: state.name, start_date: state.start_date, end_date: state.end_date,
          player_count: state.player_count, round_count: state.round_count,
          rounds, agreement_accepted: state.agreement_accepted || false,
          idempotency_key: idempotencyKey.current,
        });
        savedDraftId = resp.data.id;
      }
      clearWizardState(draftId);
      navigate(`/wizard/created?draft_id=${savedDraftId || ''}`);
    } catch (err) {
      const data = err.response?.data;
      toast.error(data?.message || data?.detail || 'Failed to save draft');
    } finally { setSaving(false); savingRef.current = false; }
  };

  const handlePay = async (targetDraftId) => {
    const id = targetDraftId || draftId;
    if (!id) return;
    setPaying(true);
    try {
      const body = promoApplied ? { promo_code: promoCode } : {};
      const r = await api.post(`/organiser/drafts/${id}/create-order`, body);
      const data = r.data;
      // Handle 100% promo activation
      if (data.activated) {
        toast.success('Event activated!');
        navigate(`/trip/${data.trip_code}`);
        return;
      }
      // Handle promo_deleted mid-flight
      if (data.error === 'promo_deleted') {
        setPromoApplied(null);
        setPromoCode('');
        setPromoError(data.detail || 'This code is no longer available');
        toast.error(data.detail || 'This code is no longer available');
        return;
      }
      const { checkout_url } = data;
      if (checkout_url) {
        window.location.href = checkout_url;
      } else {
        toast.error('No checkout URL returned');
      }
    } catch (err) {
      const data = err.response?.data;
      const detail = data?.detail;
      if (detail === 'course_pending') {
        toast.error('Course request still pending — cannot pay yet');
      } else if (detail?.code === 'email_not_verified') {
        toast.error('Please verify your email before paying');
      } else if (data?.error === 'promo_deleted') {
        setPromoApplied(null);
        setPromoCode('');
        setPromoError(data.detail || 'This code is no longer available');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Payment failed — please try again');
      }
    } finally { setPaying(false); }
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoError('');
    try {
      const body = { code: promoCode.trim() };
      if (draftId) {
        body.draft_id = draftId;
      } else {
        body.player_count = state.player_count;
        body.round_count = state.round_count;
      }
      const r = await api.post('/organiser/promo/validate', body);
      setPromoApplied(r.data);
      setPromoError('');
    } catch (err) {
      setPromoApplied(null);
      if (err.response?.data?.detail) {
        setPromoError(err.response.data.detail);
      } else {
        setPromoError("Couldn't check that code. Please try again.");
      }
    } finally { setPromoLoading(false); }
  };

  const handleRemovePromo = () => {
    setPromoApplied(null);
    setPromoCode('');
    setPromoError('');
  };

  const handleSaveAndPay = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const rounds = (state.rounds || []).map(r => ({
        format: r.format || 'stableford',
        scoring_mode: r.scoring_mode || 'individual',
        course_id: r.course_id || null,
        request_course_name: r.request_course_name || null,
        tee_name: r.tee_name || null,
      }));
      let newDraftId = draftId;
      if (draftId) {
        await api.patch(`/organiser/drafts/${draftId}`, {
          name: state.name, start_date: state.start_date, end_date: state.end_date,
          player_count: state.player_count, round_count: state.round_count, rounds,
        });
      } else {
        const r = await api.post('/organiser/drafts', {
          event_type: state.event_type || 'trip',
          name: state.name, start_date: state.start_date, end_date: state.end_date,
          player_count: state.player_count, round_count: state.round_count,
          rounds, agreement_accepted: state.agreement_accepted || false,
          idempotency_key: idempotencyKey.current,
        });
        newDraftId = r.data.id;
      }
      clearWizardState(draftId);
      setSaving(false);
      savingRef.current = false;
      await handlePay(newDraftId);
    } catch (err) {
      const data = err.response?.data;
      toast.error(data?.message || data?.detail || 'Failed to save draft');
      setSaving(false);
      savingRef.current = false;
    }
  };

  const back = () => {
    const idx = STEPS.indexOf(step);
    if (idx > 0) goStep(STEPS[idx - 1]);
    else navigate('/home');
  };

  const header = (title) => (
    <div className="bg-[#1D242B] px-4 py-4 flex items-center gap-3">
      <button onClick={back} className="text-white p-1" data-testid="wizard-back"><ArrowLeft className="w-5 h-5" weight="bold" /></button>
      <h1 className="text-lg text-white font-bold">{title}</h1>
    </div>
  );

  if (loadingDraft) return <div className="min-h-screen bg-[#F4F6F5] flex items-center justify-center"><div className="w-8 h-8 border-3 border-[#1B4332] border-t-transparent rounded-full animate-spin" /></div>;

  // ── Rounds missing a real course (used by summary step) ──
  const roundsMissingCourse = (state.rounds || []).reduce((bad, r, i) => {
    const hasCourse = r.course_id && courses.find(c => c.id === r.course_id);
    if (!hasCourse) bad.push(i + 1);
    else {
      const course = courses.find(c => c.id === r.course_id);
      const tees = course?.tees || [];
      if (tees.length > 1 && !r.tee_name) bad.push(i + 1);
      else if (tees.length > 1 && !tees.find(t => t.name === r.tee_name)) bad.push(i + 1);
    }
    return bad;
  }, []);
  const payBlocked = roundsMissingCourse.length > 0;
  const payBlockedMsg = payBlocked
    ? (roundsMissingCourse.length === 1
      ? `Round ${roundsMissingCourse[0]} needs a course`
      : roundsMissingCourse.slice(0, -1).map(r => `Round ${r}`).join(', ') + ` and Round ${roundsMissingCourse[roundsMissingCourse.length - 1]} need a course`)
    : '';

  // ── Step: Type ──
  if (step === 'type') {
    return (
      <div className="min-h-screen bg-[#F4F6F5]" data-testid="wizard-type">
        {header('New Event')}
        <div className="px-4 py-6 max-w-lg mx-auto space-y-3">
          <p className="text-sm font-semibold text-[#455561]">What type of event is this?</p>
          <button onClick={() => { update({ event_type: 'trip' }); goStep('details'); }}
            className="w-full text-left p-4 rounded-xl border-2 border-[#E1E6E4] bg-white hover:border-[#1B4332]/50 transition-all" data-testid="type-trip">
            <p className="font-bold text-[#1D242B]">Golf trip or society event</p>
            <p className="text-xs text-[#455561] mt-1">Trips with friends, society days, club events</p>
          </button>
          <button onClick={() => setShowCorporateMsg(true)}
            className="w-full text-left p-4 rounded-xl border-2 border-[#E1E6E4] bg-white hover:border-[#1B4332]/50 transition-all" data-testid="type-corporate">
            <p className="font-bold text-[#1D242B]">Corporate or charity golf day</p>
            <p className="text-xs text-[#455561] mt-1">Sponsored events, charity fundraisers, corporate days</p>
          </button>
          {showCorporateMsg && (
            <Card className="p-4 bg-[#FFF3E0] border-[#FFE0B2] rounded-xl" data-testid="corporate-msg">
              <p className="text-sm text-[#E59000] font-semibold">Corporate events are tailored to your needs.</p>
              <p className="text-sm text-[#455561] mt-1">Please contact Fore Tour at <a href="mailto:acottrill@foretour.co.uk" className="text-[#1B4332] font-semibold underline">acottrill@foretour.co.uk</a> to discuss your event.</p>
            </Card>
          )}
        </div>
      </div>
    );
  }

  // ── Step: Details ──
  if (step === 'details') {
    return (
      <div className="min-h-screen bg-[#F4F6F5]" data-testid="wizard-details">
        {header('Event Details')}
        <div className="px-4 py-6 max-w-lg mx-auto space-y-4">
          <div>
            <label className="text-sm font-semibold text-[#455561] mb-1 block">Event name</label>
            <Input value={state.name || ''} onChange={e => update({ name: e.target.value })} placeholder="e.g. Spain 2026" data-testid="wiz-name" />
          </div>
          <div>
            <label className="text-sm font-semibold text-[#455561] mb-1 block">Start date</label>
            <Input type="date" value={state.start_date || ''} onChange={e => update({ start_date: e.target.value })} data-testid="wiz-start" />
          </div>
          <div>
            <label className="text-sm font-semibold text-[#455561] mb-1 block">End date</label>
            <Input type="date" value={state.end_date || ''} onChange={e => update({ end_date: e.target.value })} data-testid="wiz-end" />
          </div>
          <Button onClick={() => goStep('size')} disabled={!state.name?.trim() || !state.start_date || !state.end_date}
            className="w-full h-12 bg-[#1B4332] text-white rounded-xl font-semibold" data-testid="wiz-next-size">Continue</Button>
        </div>
      </div>
    );
  }

  // ── Step: Size ──
  if (step === 'size') {
    return (
      <div className="min-h-screen bg-[#F4F6F5]" data-testid="wizard-size">
        {header('Players & Rounds')}
        <div className="px-4 py-6 max-w-lg mx-auto space-y-4">
          <div>
            <label className="text-sm font-semibold text-[#455561] mb-1 block">Number of players</label>
            <Input type="number" min={2} value={state.player_count || ''} onChange={e => update({ player_count: parseInt(e.target.value) || 0 })} placeholder="e.g. 12" data-testid="wiz-players" />
          </div>
          <div>
            <label className="text-sm font-semibold text-[#455561] mb-1 block">Number of rounds</label>
            <Input type="number" min={1} max={10} value={state.round_count || ''} onChange={e => {
              const rc = Math.min(10, Math.max(1, parseInt(e.target.value) || 1));
              const existing = state.rounds || [];
              const rounds = Array.from({ length: rc }, (_, i) => existing[i] || { format: 'stableford', scoring_mode: 'individual', course_id: null, request_course_name: null, tee_name: null });
              update({ round_count: rc, rounds });
            }} placeholder="e.g. 3" data-testid="wiz-rounds" />
          </div>
          <Button onClick={() => goStep('rounds')} disabled={!(state.player_count >= 2) || !(state.round_count >= 1)}
            className="w-full h-12 bg-[#1B4332] text-white rounded-xl font-semibold" data-testid="wiz-next-rounds">Continue</Button>
        </div>
      </div>
    );
  }

  // ── Step: Rounds ──
  if (step === 'rounds') {
    const rounds = state.rounds || [];
    const updateRound = (idx, patch) => {
      const updated = [...rounds];
      updated[idx] = { ...updated[idx], ...patch };
      update({ rounds: updated });
    };
    const allRoundsHaveCourse = rounds.every(r => r.course_id || (r.request_course_name && r.request_course_name.trim()));

    return (
      <div className="min-h-screen bg-[#F4F6F5]" data-testid="wizard-rounds">
        {header('Round Setup')}
        <div className="px-4 py-6 max-w-lg mx-auto space-y-4">
          {rounds.map((r, i) => (
            <Card key={i} className="p-4 bg-white border-[#E1E6E4] rounded-xl space-y-3" data-testid={`wiz-round-${i}`}>
              <p className="font-bold text-[#1D242B] text-sm">Round {i + 1}</p>
              <div>
                <label className="text-xs font-semibold text-[#455561] mb-1 block">Course</label>
                <WizardCoursePicker courses={courses} value={r.course_id} requestName={r.request_course_name}
                  onSelect={(cid) => {
                    const course = courses.find(c => c.id === cid);
                    const opts = course?.tees || [];
                    const autoTee = opts.length === 1 ? opts[0].name : null;
                    updateRound(i, { course_id: cid, request_course_name: null, tee_name: autoTee });
                  }}
                  onImportCourse={(courseId, courseData) => {
                    if (courseData) setCourses(prev => [...prev, courseData]);
                    else api.get('/courses').then(r => setCourses(r.data)).catch(() => {});
                    const c = courseData || courses.find(x => x.id === courseId);
                    const autoTee = c?.tees?.length === 1 ? c.tees[0].name : null;
                    updateRound(i, { course_id: courseId, request_course_name: null, tee_name: autoTee });
                  }}
                  onRequestCourse={() => updateRound(i, { course_id: null, tee_name: null })}
                  onRequestNameChange={(val) => updateRound(i, { request_course_name: val })}
                  roundIndex={i}
                  testIdPrefix={`wiz-course-${i}`} />
              </div>
              {/* Tee picker — shown only when course has >1 tee */}
              {(() => {
                const course = r.course_id ? courses.find(c => c.id === r.course_id) : null;
                const opts = course?.tees || [];
                if (opts.length <= 1) return null;
                return (
                  <div>
                    <label className="text-xs font-semibold text-[#455561] mb-1 block">Tee</label>
                    <select value={r.tee_name || ''} onChange={e => updateRound(i, { tee_name: e.target.value || null })}
                      className="w-full h-10 px-3 rounded-lg border border-[#E1E6E4] text-sm bg-white" data-testid={`wiz-tee-${i}`}>
                      <option value="">Select tee…</option>
                      {opts.map((t, ti) => (
                        <option key={ti} value={t.name}>{t.name}{t.course_rating != null && t.slope_rating != null ? ` · CR ${t.course_rating} · S ${t.slope_rating}` : ''}</option>
                      ))}
                    </select>
                  </div>
                );
              })()}
              <div>
                <label className="text-xs font-semibold text-[#455561] mb-1 block">Format</label>
                <select value={r.format || 'stableford'} onChange={e => updateRound(i, { format: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg border border-[#E1E6E4] text-sm bg-white" data-testid={`wiz-format-${i}`}>
                  <option value="stableford">Stableford</option>
                  <option value="nsp">Net Stroke Play</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#455561] mb-1 block">Scoring mode</label>
                <div className="flex gap-2">
                  <button onClick={() => updateRound(i, { scoring_mode: 'individual' })}
                    className={`flex-1 py-2 rounded-lg text-xs font-semibold border-2 transition-all ${r.scoring_mode !== 'texas_scramble' ? 'border-[#1B4332] bg-[#1B4332] text-white' : 'border-[#E1E6E4] text-[#455561]'}`}
                    data-testid={`wiz-mode-ind-${i}`}>Individual</button>
                  <button onClick={() => updateRound(i, { scoring_mode: 'texas_scramble' })}
                    className={`flex-1 py-2 rounded-lg text-xs font-semibold border-2 transition-all ${r.scoring_mode === 'texas_scramble' ? 'border-[#1B4332] bg-[#1B4332] text-white' : 'border-[#E1E6E4] text-[#455561]'}`}
                    data-testid={`wiz-mode-scr-${i}`}>Texas Scramble</button>
                </div>
              </div>
            </Card>
          ))}
          <Button onClick={() => goStep('summary')} disabled={!allRoundsHaveCourse}
            className="w-full h-12 bg-[#1B4332] text-white rounded-xl font-semibold" data-testid="wiz-next-summary">Continue</Button>
        </div>
      </div>
    );
  }

  // ── Step: Summary ──
  if (step === 'summary') {
    return (
      <div className="min-h-screen bg-[#F4F6F5]" data-testid="wizard-summary">
        {header(draftId ? 'Edit Draft' : 'Summary')}
        <div className="px-4 py-6 max-w-lg mx-auto space-y-4">
          {organiser && !organiser.email_verified && (
            <Card className="p-3 bg-[#FFF3E0] border-[#FFE0B2] rounded-xl">
              <p className="text-xs text-[#E59000]">Verify your email before you can pay for this event. <button onClick={() => api.post('/organiser/resend-verification', { email: organiser.email }).then(() => toast.success('Verification email sent'))} className="underline font-semibold">Resend verification</button></p>
            </Card>
          )}
          <Card className="p-4 bg-white border-[#E1E6E4] rounded-xl space-y-2">
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-[#1D242B]">{state.name}</h2>
              <button onClick={() => goStep('details')} className="text-xs text-[#1B4332] font-semibold hover:underline" data-testid="edit-details">Edit</button>
            </div>
            <p className="text-xs text-[#455561]">{state.start_date} — {state.end_date}</p>
          </Card>
          <Card className="p-4 bg-white border-[#E1E6E4] rounded-xl">
            <div className="flex items-center justify-between">
              <p className="text-xs text-[#455561]">{state.player_count} players · {state.round_count} round{state.round_count !== 1 ? 's' : ''}</p>
              <button onClick={() => goStep('size')} className="text-xs text-[#1B4332] font-semibold hover:underline" data-testid="edit-size">Edit</button>
            </div>
          </Card>
          {(state.rounds || []).map((r, i) => {
            const needsCourse = roundsMissingCourse.includes(i + 1);
            return (
              <Card key={i} className={`p-3 bg-white border-[#E1E6E4] rounded-xl ${needsCourse ? 'border-[#D32F2F]' : ''}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-[#1D242B]">Round {i + 1}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-[#455561]">{r.format === 'nsp' ? 'NSP' : 'Stableford'} · {r.scoring_mode === 'texas_scramble' ? 'Scramble' : 'Individual'}</span>
                    <button onClick={() => goStep('rounds')} className="text-xs text-[#1B4332] font-semibold hover:underline" data-testid={`edit-round-${i}`}>Edit</button>
                  </div>
                </div>
                {needsCourse ? (
                  <p className="text-xs text-[#D32F2F] font-semibold mt-1" data-testid={`round-needs-course-${i}`}>Choose a course for this round</p>
                ) : (
                  <p className="text-xs text-[#455561] mt-1">{r.course_id ? courses.find(c => c.id === r.course_id)?.name || r.course_id : `Requested: ${r.request_course_name || '—'}`}</p>
                )}
              </Card>
            );
          })}
          {pricing && (
            <Card className="p-4 bg-white border-[#E1E6E4] rounded-xl space-y-2" data-testid="pricing-card">
              <h3 className="font-bold text-[#1D242B] text-sm">Pricing</h3>
              {pricing.breakdown.map((b, i) => (
                <div key={i} className="flex justify-between text-xs text-[#455561]">
                  <span>Round {b.round_number} ({state.player_count} players)</span>
                  <span>£{(b.price_pence / 100).toFixed(2)}</span>
                </div>
              ))}
              <div className="flex justify-between text-sm font-bold text-[#1D242B] pt-2 border-t border-[#E1E6E4]">
                <span>Total</span>
                <span>£{(pricing.total_pence / 100).toFixed(2)}</span>
              </div>
            </Card>
          )}
          {/* Promo code input */}
          {pricing && (
            <Card className="p-4 bg-white border-[#E1E6E4] rounded-xl space-y-2" data-testid="promo-card">
              <h3 className="font-bold text-[#1D242B] text-sm">Promo Code</h3>
              {promoApplied ? (
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-mono font-bold text-[#1B4332]">{promoCode}</span>
                    <span className="text-xs text-[#455561] ml-2">{promoApplied.percent_off}% off</span>
                    <span className="text-xs text-[#1B4332] font-bold ml-2">−£{(promoApplied.discount_pence / 100).toFixed(2)}</span>
                  </div>
                  <button onClick={handleRemovePromo} className="text-xs text-[#D32F2F] font-semibold" data-testid="promo-remove">Remove</button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input value={promoCode} onChange={e => { setPromoCode(e.target.value); setPromoError(''); }}
                    placeholder="Enter code" className="flex-1 h-10 font-mono text-sm" data-testid="promo-input" />
                  <Button onClick={handleApplyPromo} disabled={promoLoading || !promoCode.trim()}
                    className="h-10 px-4 bg-[#1B4332] text-white rounded-lg text-sm font-semibold" data-testid="promo-apply-btn">
                    {promoLoading ? '...' : 'Apply'}
                  </Button>
                </div>
              )}
              {promoError && <p className="text-xs text-[#D32F2F]" data-testid="promo-error">{promoError}</p>}
              {promoApplied && (
                <div className="flex justify-between text-sm font-bold text-[#1B4332] pt-1 border-t border-[#E1E6E4]">
                  <span>Discounted Total</span>
                  <span>£{(promoApplied.discounted_total_pence / 100).toFixed(2)}</span>
                </div>
              )}
            </Card>
          )}
          {!draftId && (
            <Card className="p-4 bg-white border-[#E1E6E4] rounded-xl">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={state.agreement_accepted || false} onChange={e => update({ agreement_accepted: e.target.checked })}
                  className="mt-0.5 w-4 h-4 rounded border-[#E1E6E4]" data-testid="wiz-agree" />
                <span className="text-xs text-[#455561]">
                  I agree to the <a href={settings?.terms_url || '#'} target="_blank" rel="noopener noreferrer" className="text-[#1B4332] underline">Terms of Use and Sale</a> and <a href={settings?.privacy_url || '#'} target="_blank" rel="noopener noreferrer" className="text-[#1B4332] underline">Privacy Policy</a>, and I have told my players their names and handicaps will be added.
                </span>
              </label>
            </Card>
          )}
          {/* Payment status banner */}
          {draftId && latestPayment?.status === 'pending' && (
            <Card className="p-3 bg-[#E3F2FD] border-[#90CAF9] rounded-xl">
              <p className="text-xs text-[#1565C0] font-medium">Payment pending with Revolut. <button onClick={() => window.location.reload()} className="underline font-semibold" data-testid="refresh-payment">Refresh</button></p>
            </Card>
          )}
          {draftId && latestPayment?.status === 'failed' && (
            <Card className="p-3 bg-[#FFEBEE] border-[#EF9A9A] rounded-xl">
              <p className="text-xs text-[#C62828] font-medium">Last payment failed. <button onClick={() => handlePay()} className="underline font-semibold" data-testid="retry-payment">Try again</button></p>
            </Card>
          )}
          {/* Save + Pay buttons */}
          {payBlocked && (
            <p className="text-xs text-[#D32F2F] font-semibold text-center" data-testid="pay-blocked-msg">{payBlockedMsg}</p>
          )}
          {draftId && latestPayment?.status !== 'pending' ? (
            <>
              <Button onClick={handleSave} disabled={saving}
                className="w-full h-12 bg-[#1B4332] text-white rounded-xl font-semibold" data-testid="wiz-save">
                {saving ? 'Saving...' : 'Save changes'}
              </Button>
              <Button onClick={() => handlePay()} disabled={paying || payBlocked || !organiser?.email_verified}
                className="w-full h-12 bg-[#0D47A1] text-white rounded-xl font-semibold flex items-center justify-center gap-2" data-testid="wiz-pay">
                <CreditCard className="w-5 h-5" weight="bold" />
                {paying ? 'Redirecting...' : `Pay £${promoApplied ? (promoApplied.discounted_total_pence / 100).toFixed(2) : pricing ? (pricing.total_pence / 100).toFixed(2) : '...'} now`}
              </Button>
            </>
          ) : !draftId ? (
            <>
              <Button onClick={handleSaveAndPay} disabled={saving || paying || payBlocked || !state.agreement_accepted || !organiser?.email_verified}
                className="w-full h-12 bg-[#0D47A1] text-white rounded-xl font-semibold flex items-center justify-center gap-2" data-testid="wiz-save-pay">
                <CreditCard className="w-5 h-5" weight="bold" />
                {saving || paying ? 'Processing...' : `Save & Pay £${promoApplied ? (promoApplied.discounted_total_pence / 100).toFixed(2) : pricing ? (pricing.total_pence / 100).toFixed(2) : '...'} now`}
              </Button>
              <Button onClick={handleSave} disabled={saving || !state.agreement_accepted} variant="outline"
                className="w-full h-12 rounded-xl border-[#E1E6E4] text-[#455561] font-semibold" data-testid="wiz-save">
                {saving ? 'Saving...' : 'Save draft (pay later)'}
              </Button>
            </>
          ) : null}
          {draftId && (
            <Button onClick={() => navigate('/account')} variant="outline"
              className="w-full h-12 rounded-xl border-[#E1E6E4] text-[#455561] font-semibold" data-testid="back-to-account">
              Back to My Account
            </Button>
          )}
        </div>
      </div>
    );
  }

  return null;
}

export function WizardCreated() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { organiser } = useAuth();
  const createdDraftId = params.get('draft_id');
  const [paying, setPaying] = useState(false);

  const handlePay = async () => {
    if (!createdDraftId) return;
    setPaying(true);
    try {
      const r = await api.post(`/organiser/drafts/${createdDraftId}/create-order`);
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
      else toast.error('No checkout URL returned');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code === 'email_not_verified') toast.error('Please verify your email before paying');
      else toast.error(typeof detail === 'string' ? detail : 'Payment failed');
    } finally { setPaying(false); }
  };

  const canPay = organiser?.email_verified && createdDraftId;

  return (
    <div className="min-h-screen bg-[#F4F6F5] flex items-center justify-center px-4" data-testid="wizard-created">
      <Card className="w-full max-w-md p-8 bg-white border-[#E1E6E4] rounded-xl text-center space-y-4">
        <div className="w-14 h-14 bg-[#E8F5E9] rounded-full flex items-center justify-center mx-auto">
          <Check className="w-7 h-7 text-[#2E7D32]" weight="bold" />
        </div>
        <h2 className="text-xl font-bold text-[#1D242B]">Draft saved</h2>
        <p className="text-sm text-[#455561]">Pay now to activate your event, or come back later. Drafts expire in 30 days.</p>
        {organiser && !organiser.email_verified && (
          <p className="text-xs text-[#E59000]">Verify your email to enable payment.</p>
        )}
        {createdDraftId && (
          <Button onClick={handlePay} disabled={paying || !canPay}
            className="w-full bg-[#0D47A1] text-white rounded-xl font-semibold flex items-center justify-center gap-2" data-testid="created-pay-btn">
            <CreditCard className="w-5 h-5" weight="bold" />
            {paying ? 'Redirecting...' : 'Pay now'}
          </Button>
        )}
        <Button onClick={() => navigate('/account')} variant="outline" className="w-full rounded-xl border-[#E1E6E4] text-[#455561] font-semibold" data-testid="goto-account">My account</Button>
      </Card>
    </div>
  );
}
